import type { CitationRun, CitationRunStatus } from '../../../api/measurement';

const PROVIDER_LABEL: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Gemini',
  perplexity: 'Perplexity',
};

export function isRunActive(status: CitationRunStatus | undefined): boolean {
  return status === 'running';
}

interface Props {
  run: CitationRun | null;
  triggerError?: Error | null;
}

export function SweepStatusPill({ run, triggerError }: Props) {
  if (triggerError) {
    return (
      <Container tone="error">
        <strong>Sweep didn't start.</strong> {triggerError.message}
      </Container>
    );
  }
  if (!run) return null;

  if (run.status === 'running') {
    const elapsed = formatElapsed(run.startedAt, null);
    return (
      <Container tone="info">
        <span>
          <strong>Sweep running…</strong> {run.done}/{run.expected || '?'} probes
          {run.percent > 0 ? ` · ${run.percent}%` : ''} · {elapsed}
        </span>
        {run.providersUsed.length > 0 && (
          <span className="text-text-tertiary"> · {run.providersUsed.map((p) => PROVIDER_LABEL[p] ?? p).join(', ')}</span>
        )}
      </Container>
    );
  }

  if (run.status === 'failed') {
    return (
      <Container tone="error">
        <strong>Sweep failed.</strong> {run.errorMessage ?? 'No error message recorded.'}
      </Container>
    );
  }

  const tone = run.status === 'completed_with_errors' ? 'warn' : 'success';
  const elapsed = formatElapsed(run.startedAt, run.finishedAt);
  return (
    <Container tone={tone}>
      <span>
        <strong>{run.status === 'completed_with_errors' ? 'Completed with issues' : 'Completed'}.</strong>{' '}
        {run.cited}/{run.total} cited · ${run.costUsd.toFixed(2)} · {elapsed}
      </span>
      {run.errorMessage && (
        <span className="block text-body-xs mt-1 text-text-secondary">{run.errorMessage}</span>
      )}
    </Container>
  );
}

function Container({ children, tone }: { children: React.ReactNode; tone: 'info' | 'success' | 'warn' | 'error' }) {
  const palette: Record<typeof tone, string> = {
    info: 'border-info/20 bg-info-light text-info-dark',
    success: 'border-success/20 bg-success-light text-success-dark',
    warn: 'border-warning/20 bg-warning-light text-warning-dark',
    error: 'border-error/20 bg-error-light text-error-dark',
  };
  return (
    <div className={`rounded-md border px-3 py-1.5 text-body-sm ${palette[tone]}`}>{children}</div>
  );
}

function formatElapsed(startedAt: string, finishedAt: string | null): string {
  const start = new Date(startedAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const seconds = Math.max(0, Math.round((end - start) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}
